import React from 'react';
import { AgentCustomerAnalysis } from '../../types/organizations';
import { getCategoryStyling } from '../../utils/categoryStyling';


interface CustomerAnalysisItemCardProps {
  analysis: AgentCustomerAnalysis;
  index: number;
}

const CustomerAnalysisItemCard: React.FC<CustomerAnalysisItemCardProps> = ({ analysis, index }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAnalysisTypeDisplayName = (type: string) => {
    switch (type) {
      case 'customer_insight':
        return 'Customer Insight Analysis';
      case 'payment_reliability_score':
        return 'Payment Reliability Assessment';
      case 'financial_risk_score':
        return 'Financial Risk Assessment';
      case 'recommended_actions':
        return 'Recommendations Analysis';
      default:
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-6">
      {/* Analysis Output - Direct content without nested cards */}
      {analysis.output_json && (
        <div className="mb-4">
          {analysis.analysis_type === 'customer_insight' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Customer Category</h3>
                {analysis.output_json.selected_category && (
                  <div className={`px-4 py-2 ${getCategoryStyling(analysis.output_json.selected_category).bg} ${getCategoryStyling(analysis.output_json.selected_category).text} text-lg font-bold rounded-full`}>
                    {analysis.output_json.selected_category.replace(/customer/gi, '').trim()}
                  </div>
                )}
              </div>
              {analysis.output_json.customer_profile_summary && (
                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded mb-4">
                  {analysis.output_json.customer_profile_summary}
                </div>
              )}
            </div>
          )}

          {analysis.analysis_type === 'payment_reliability_score' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Payment Risk Assessment</h3>
                <div className="text-2xl font-bold text-orange-900">
                  {typeof analysis.output_json.score === 'string'
                    ? parseInt(analysis.output_json.score) || 0
                    : analysis.output_json.score || 0}/10
                </div>
              </div>
            </div>
          )}

          {analysis.analysis_type === 'financial_risk_score' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Financial Risk Assessment</h3>
                <div className="text-2xl font-bold text-red-900">
                  {typeof analysis.output_json.score === 'string'
                    ? parseInt(analysis.output_json.score) || 0
                    : analysis.output_json.score || 0}/10
                </div>
              </div>
 
            </div>
          )}

          {analysis.analysis_type === 'recommended_actions' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h3>
              {analysis.output_json.actions && (
                <div className="space-y-2">
                  {analysis.output_json.actions.map((action: string, index: number) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 mr-3">
                        <span className="text-gray-600 text-sm font-medium">{index + 1}.</span>
                      </div>
                      <p className="text-sm text-gray-700">{action}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Analysis Timestamp - Below remark section */}
      {/* <div className="text-sm text-gray-500 mb-4">
        {formatDate(analysis.analyzed_at)}
      </div> */}

      {/* Reasonings - Compact without background (hidden for recommendations) */}
      {analysis.analysis_type !== 'recommended_actions' && analysis.reasonings && analysis.reasonings.length > 0 && (
        <div className="mb-4">
          <h4 className="text-md font-semibold text-gray-900 mb-3">Reasonings</h4>
          <div className="space-y-2">
            {analysis.reasonings.map((reasoning, reasoningIndex) => {
              // Handle both string and object reasonings
              if (typeof reasoning === 'string') {
                return (
                  <div key={reasoningIndex} className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      <span className="text-gray-600 text-sm font-medium">{reasoningIndex + 1}.</span>
                    </div>
                    <p className="text-sm text-gray-700">{reasoning}</p>
                  </div>
                );
              } else if (typeof reasoning === 'object' && reasoning !== null) {
                // Handle object reasonings by rendering each key-value pair
                return (
                  <div key={reasoningIndex} className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      <span className="text-gray-600 text-sm font-medium">{reasoningIndex + 1}.</span>
                    </div>
                    <div className="flex-1">
                      {Object.entries(reasoning).map(([key, value]) => (
                        <div key={key} className="mb-2 last:mb-0">
                          <h6 className="font-medium text-gray-900 text-sm mb-1">{key}:</h6>
                          <div className="text-sm text-gray-700 ml-2">
                            {typeof value === 'object' && value !== null ? (
                              // Handle nested objects (like criteria)
                              <div className="ml-2">
                                {Object.entries(value).map(([nestedKey, nestedValue]) => (
                                  <div key={nestedKey} className="mb-1">
                                    <span className="font-medium text-gray-800">{nestedKey}:</span>
                                    <span className="ml-1">{String(nestedValue)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span>{String(value)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              } else {
                // Fallback for any other type
                return (
                  <div key={reasoningIndex} className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      <span className="text-gray-600 text-sm font-medium">{reasoningIndex + 1}.</span>
                    </div>
                    <p className="text-sm text-gray-700">{String(reasoning)}</p>
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}

      {/* No Reasonings Available (hidden for recommendations) */}
      {analysis.analysis_type !== 'recommended_actions' && (!analysis.reasonings || analysis.reasonings.length === 0) && (
        <div className="text-center py-4 mb-4">
          <div className="text-gray-500 text-sm">No reasonings available for this analysis.</div>
        </div>
      )}

      {analysis.output_json?.remark && (
        <div className="mb-4">
          <h4 className="text-md font-semibold text-gray-900 mb-2">Remark</h4>
          <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded mb-4">
            {analysis.output_json.remark}
          </div>
        </div>
      )}

      {/* Analysis Info - At bottom */}
      <div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="bg-gray-50 p-3 rounded-lg">
            <span className="text-gray-600">Analyzed At:</span>
            <div className="font-medium text-gray-900">
              {formatDate(analysis.analyzed_at)}
            </div>
          </div>
          {analysis.crew_run_id && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-gray-600">Crew Run ID:</span>
              <div className="font-medium text-gray-900 font-mono text-xs">
                {analysis.crew_run_id}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerAnalysisItemCard;
